import { GET_ALL_LOCATIONS } from "services/graphQL/ciq-queries";
import { useMemo, useState, useContext, useEffect, useCallback } from "react";
import { gql } from "@apollo/client";
import { SAVE_LOCATION_MATERIALS } from "entity-app/graphQL/ciq-feature-mutations";
import { ProjectContext } from "context/ProjectProvider";
import { formatLBSTreeData } from "utils/utils";
import { getLBSTree } from "services/common";
import { useCIQMutation, useCIQQuery } from "./ciq-gql-hooks";

interface LocationWithPath {
  path: string;
  name: string;
  id: string;
}

interface LocationsResponse {
  get_locations_with_paths: LocationWithPath[];
}

export const useLocationBreakdownStructure = (
  gqlClientForProject: any,
  projectId: string,
  hasPermissionToSetLBSForMaterial: boolean
) => {
  const { isFeatureFlagEnabled, eventLogs, tokenRetrievalState } =
    useContext(ProjectContext);

  const isLBSFeatureEnabled = isFeatureFlagEnabled(
    "LOCATION_BREAKDOWN_STRUCTURE"
  );

  const [savingLBS, setSavingLBS] = useState(false);
  const [LBSDataInTreeFormat, setLBSDataInTreeFormat] = useState<any[]>([]);

  const {
    data: LBSLocationsResponse,
    loading: loadingLBSMaster,
    refetch: refetchLBSLocations
  } = useCIQQuery<LocationsResponse>(GET_ALL_LOCATIONS, {
    client: gqlClientForProject,
    skip:
      !gqlClientForProject ||
      !isLBSFeatureEnabled ||
      !hasPermissionToSetLBSForMaterial
  });

  const [mutationSaveLocationMaterials] = useCIQMutation(
    gql(SAVE_LOCATION_MATERIALS),
    {
      client: gqlClientForProject
    }
  );

  const getLBSTreeData = useCallback(async () => {
    if (!isLBSFeatureEnabled || !hasPermissionToSetLBSForMaterial) return;
    const res = await getLBSTree(tokenRetrievalState?.token);
    if (res?.success) {
      const formattedTreeData = formatLBSTreeData(
        res?.data?.children.sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        ),
        (node) => ({
          ...node,
          value: node.id,
          title: node.name,
          children: []
        })
      );
      setLBSDataInTreeFormat(formattedTreeData);
    }
  }, [
    tokenRetrievalState?.token,
    isLBSFeatureEnabled,
    hasPermissionToSetLBSForMaterial
  ]);

  const LBSOptionsMaster: { label: string; value: string }[] = useMemo(() => {
    if (LBSLocationsResponse?.get_locations_with_paths?.length) {
      return LBSLocationsResponse?.get_locations_with_paths?.map(
        (location: LocationWithPath) => ({
          label: location.path,
          value: location.id
        })
      );
    }
    return [];
  }, [LBSLocationsResponse]);

  const setLBSForMaterial = useCallback(
    async (materialId: string[], selectedLBSIds: string[]) => {
      if (!isLBSFeatureEnabled || !hasPermissionToSetLBSForMaterial)
        return null;

      setSavingLBS(true);
      const updateResponse = await mutationSaveLocationMaterials({
        variables: {
          feature_instance_ids: materialId,
          feature_type: "MATERIALS",
          location_ids: selectedLBSIds
        }
      });
      setSavingLBS(false);
      return updateResponse;
    },
    [
      isLBSFeatureEnabled,
      mutationSaveLocationMaterials,
      hasPermissionToSetLBSForMaterial
    ]
  );

  useEffect(() => {
    if (!tokenRetrievalState?.token) return;
    getLBSTreeData();
  }, [getLBSTreeData, tokenRetrievalState?.token]);

  useEffect(() => {
    if (eventLogs.length > 0) {
      const eventLogForLBSMaster = eventLogs.find(
        (eventLog) => eventLog.data_source === "location"
      );

      if (eventLogForLBSMaster) {
        refetchLBSLocations();
        getLBSTreeData();
      }
    }
  }, [eventLogs, projectId, refetchLBSLocations, getLBSTreeData]);

  return {
    isLBSFeatureEnabled,
    LBSLocationsResponse,
    LBSOptionsMaster,
    setLBSForMaterial,
    savingLBS,
    loadingLBSMaster,
    LBSDataInTreeFormat
  };
};
